import { translate } from '@jsverse/transloco';
import { forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import { Invoice } from 'src/app/models/invoice.interface';
import {
  TransactionStatusTypeId,
  TransactionTypeId,
} from 'src/app/models/transaction-enums.model';
import {
  Transaction,
  TransactionStatus,
} from 'src/app/models/transaction.interface';

import { InvoiceService } from 'src/app/services/invoice/invoice.service';
import { InvoicingService } from 'src/app/services/invoicing/invoicing.service';
import { LocationService } from 'src/app/services/location/location.service';
import { ProviderService } from 'src/app/services/provider/provider.service';
import { TransactionService } from 'src/app/services/transaction/transaction.service';

export abstract class BaseInvoiceComponent {
  constructor(
    protected invoiceService: InvoiceService,
    protected invoicingService: InvoicingService,
    protected locationService: LocationService,
    protected transactionService: TransactionService,
    protected providerService: ProviderService
  ) {}

  protected getInvoiceDetails(
    invoiceTrackingKey: string,
    invoicedViewed: boolean
  ): Observable<any> {
    // 1. Get Login and then invoice

    const invoice$ = this.invoiceService
      .postLoginInvoice(invoiceTrackingKey)
      .pipe(
        tap((login) => {
          this.invoiceService.setLoginData(login);         
        }),
        switchMap((login) => {
          return this.invoiceService.getInvoice(login.invoiceId);
        })
      );

    // 2. get location and transaction

    const locationTransacion$ = invoice$.pipe(
      tap((invoice: Invoice) => {
        this.invoiceService.setInvoiceData(invoice);       
      }),
      switchMap((invoice: Invoice) => {
        return this.getLocationTransaction(invoice);
      })
    );

    // 3. get invoicing and configuration

    const configuration$ = locationTransacion$
      .pipe(switchMap((_) => this.getInvocingAndConfiguration()))
      .pipe(
        tap(({ invoicing, provider }) => {
          this.invoicingService.setInvoicingData(invoicing);
          this.providerService.setProviderData(provider);
        })
      );

    // 4. set Customer Viewed Date

    const result$ = configuration$
      .pipe(switchMap((_) => this.setCustomerViewedDate(invoicedViewed)))
      .pipe(switchMap((_) => of(null)));

    return result$;
  }

  private getLocationTransaction(invoice: Invoice) {
    const location$ = this.locationService.getAccountLocation().pipe(
      tap((location) => {    
        this.locationService.setAccountLocation(location);
      })
    );
    const transaction$ = this.transactionService
      .getTransaction(this.invoiceService.login.transactionId)
      .pipe(
        tap((transaction) => {         
          this.transactionService.setTransaction(transaction);
        })
      );

    return forkJoin({
      location: location$,
      transaction: transaction$,
    }).pipe(
      tap(({ location, transaction }) => {
        const transactionStatusItems =
          BaseInvoiceComponent.getTransactionStatusItems(
            transaction,
            invoice
          );

        this.transactionService.setTransactionStatusItems(
          transactionStatusItems
        );
      
      })
    );
  }

  private getInvocingAndConfiguration() {
    return forkJoin({
      invoicing: this.invoicingService.getInvoicing(),
      provider: this.providerService.getProviderConfiguration()
    });
  }

  private setCustomerViewedDate(
    invoiceViewed: boolean
  ): Observable<[any, any]> | Observable<[any]> | Observable<null> {
    const now = new Date().toISOString();
    const patch$ = invoiceViewed
      ? this.invoiceService.invoice.firstViewedDateTime == null
        ? // update invoice view
          forkJoin([
            this.invoiceService
              .patchInvoice(this.invoiceService.login.invoiceId, now, now, null),
            // save note
            this.transactionService
              .postTransactionNote(
                this.invoiceService.login.transactionId,
                translate('TxnNoteCustomerViewed'),
                translate('Viewed'),
                100024,
                this.locationService.location.localCurrentDate
              )              
          ])
        : forkJoin([
            // update invoice view
            this.invoiceService
              .patchInvoice(
                this.invoiceService.login.invoiceId,
                null,
                now,
                null
              )     
          ])
      : of(null);

    return patch$;
  }

  protected getPayButtonStatus(
    transactionStatusTypeId: TransactionStatusTypeId
  ): boolean {
    switch (transactionStatusTypeId) {
      case TransactionStatusTypeId.Suspended:
      case TransactionStatusTypeId.PartiallyCompleted:
        return true;
    }
    return false;
  }

  public static getTransactionStatusItems(
    transaction: Transaction,
    invoice: Invoice
  ): TransactionStatus {
    let transactionStatus = {
      primaryText: '',
      secondaryText: '',
      statusLabel: '',
      statusColor: '',
    };

    if (transaction.transactionTypeId === TransactionTypeId.Normal) {
      switch (transaction.transactionStatusTypeId) {
        case TransactionStatusTypeId.Suspended:
          if (invoice.remainingDueDays < 0) {
            transactionStatus = {
              primaryText: translate('InvoiceStatusOverdue'),
              secondaryText: '',
              statusLabel: translate('Overdue'),
              statusColor: 'red',
            };
            return transactionStatus;
          } else if (invoice.sinceFirstViewedDays === 0 || invoice.sinceFirstViewedDays == null) {
            transactionStatus = {
              primaryText: translate('InvoiceStatusNew'),
              secondaryText: '',
              statusLabel: translate('Outstanding'),
              statusColor: 'orange',
            };
          } else if (invoice.sinceFirstViewedDays != null && invoice.sinceFirstViewedDays <= 0) {
            if (invoice.remainingDueDays === 1) {
              transactionStatus = {
                primaryText: translate('Reminder'),
                secondaryText: translate('SuspendedInvoiceDueNDaysText', { value1: invoice.remainingDueDays }),
                statusLabel: translate('Outstanding'),
                statusColor: 'orange',
              };
            } else if (invoice.remainingDueDays > 1) {
              transactionStatus = {
                primaryText: translate('Reminder'),
                secondaryText: translate('SuspendedInvoiceSecondaryText', { value1 : invoice.remainingDueDays }) ,
                statusLabel: translate('Outstanding'),
                statusColor: 'orange',
              };
            } else if (invoice.remainingDueDays === 0) {
              transactionStatus = {
                primaryText: translate('Reminder'),
                secondaryText: translate('SuspendedInvoiceDueTodayText'),
                statusLabel: translate('Outstanding'),
                statusColor: 'orange',
              };
            }
          }
          return transactionStatus;
        case TransactionStatusTypeId.PartiallyCompleted:
          if (invoice.remainingDueDays < 0)
          {
            transactionStatus = {
              primaryText: translate('InvoiceStatusOverdue'),
              secondaryText: '',
              statusLabel: translate('Overdue'),
              statusColor: 'red',
            };
          }
          else
          // TODO: this is based on BRD "if (invoice.sinceFirstViewedDays != null && invoice.sinceFirstViewedDays <= 0)" but if you view and pay as merch chid 1004 then this will not work. 
          {
            transactionStatus = {
              primaryText: translate('InvoiceStatusPartiallyPaid'),
              secondaryText: translate('PartiallyCompletedInvoiceSecondaryText', { value1: transaction.balanceDueCurrencyFormat }),
              statusLabel: translate('PartiallyPaid'),
              statusColor: 'blue-39',
            };
          }
          return transactionStatus;
        case TransactionStatusTypeId.Completed:
          transactionStatus = {
            primaryText: translate('InvoiceStatusPaid'),
            secondaryText: '',
            statusLabel: translate('Paid'),
            statusColor: 'evergreen',
          };
          return transactionStatus;
        case TransactionStatusTypeId.Resolved:
          transactionStatus = {
            primaryText: translate('InvoiceStatusResolved'),
            secondaryText: '',
            statusLabel: translate('Refunded'),
            statusColor: 'red',
          };
          return transactionStatus;
        case TransactionStatusTypeId.Cancelled:
          transactionStatus = {
            primaryText: translate('InvoiceStatusCancelled'),
            secondaryText: translate('CancelledInvoiceSecondaryText'),
            statusLabel: translate('Cancelled'),
            statusColor: 'orange',
          };
          return transactionStatus;
        case TransactionStatusTypeId.BadDebt:
          transactionStatus = {
            primaryText: translate('InvoiceStatusWrittenOff'),
            secondaryText: '',
            statusLabel: translate('BadDebt'),
            statusColor: 'red',
          };
          return transactionStatus;
        case TransactionStatusTypeId.Failed:
          transactionStatus = {
            primaryText: translate('InvoiceStatusFailed'),
            secondaryText: '',
            statusLabel: translate('Refunded'),
            statusColor: 'red',
          };
          return transactionStatus;
        case TransactionStatusTypeId.Refunded:
        case TransactionStatusTypeId.RefundedAmount:
        case TransactionStatusTypeId.RefundedItems:
          transactionStatus = {
            primaryText: translate('InvoiceStatusRefunded'),
            secondaryText: '',
            statusLabel: translate('Refunded'),
            statusColor: 'red',
          };
          return transactionStatus;
      }
    } else if (
      transaction.transactionTypeId === TransactionTypeId.Refund ||
      transaction.transactionTypeId === TransactionTypeId.OpenItemRefund ||
      transaction.transactionTypeId === TransactionTypeId.OpenAmountRefund
    ) {
      switch (transaction.transactionStatusTypeId) {
        case TransactionStatusTypeId.Completed:
          transactionStatus = {
            primaryText: translate('InvoiceStatusRefunded'),
            secondaryText: '',
            statusLabel: translate('Refunded'),
            statusColor: 'red',
          };
          return transactionStatus;
        case TransactionStatusTypeId.Failed:
        case TransactionStatusTypeId.PartiallyFailed:
          transactionStatus = {
            primaryText: translate('InvoiceStatusRefunded'),
            secondaryText: '',
            statusLabel: translate('Refunded'),
            statusColor: 'red',
          };
          return transactionStatus;
      }
    }

    return transactionStatus;
  }
  
}
